import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { SelectAllHotkeys } from 'src/components/common/MultiSelect/Toggle/contants';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import {
  MultiSelectScope,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { PosHotkeys } from 'src/core/POS/PosHotkeys';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { CrossIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { CommandOptionType } from 'src/WebApiController';

import * as styles from './EventActions.css';

interface BulkActionToggleButtonProps {
  groupId: string;
  show?: boolean;
  tableHeader?: boolean;
}

export const BulkActionToggleButton = ({
  groupId,
  show = true,
  tableHeader = false,
}: BulkActionToggleButtonProps) => {
  const { selectionMode, setSelectionMode, getSelection } =
    useMultiSelectionContext();
  const listingSelection = getSelection(groupId);
  const isChecked = selectionMode?.groupId === groupId;
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const onSelectMultipleClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (listingSelection.isSingleGroupMultiSelect) {
      setSelectionMode();
    } else {
      setSelectionMode({
        mode: MultiSelectScope.SingleGroup,
        groupId,
      });
    }
  };

  const keyNames = SelectAllHotkeys;

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ToggleSelectionMode,
        onSelect: () => {
          if (listingSelection.isSingleGroupMultiSelect) {
            setSelectionMode();
          } else {
            setSelectionMode({
              mode: MultiSelectScope.SingleGroup,
              groupId,
            });
          }
        },
      },
    });

    return () => {
      unregisterCommand({ commandId: CommandOptionType.ToggleSelectionMode });
    };
  }, [
    unregisterCommand,
    registerCommand,
    setSelectionMode,
    listingSelection.isSingleGroupMultiSelect,
    groupId,
  ]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          layout={false}
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: 'auto' }}
          exit={{ opacity: 0, width: 0 }}
          transition={{ duration: 0.3 }}
        >
          <PosHotkeys
            keyNames={keyNames}
            onHotkeyActivated={(_keyName, _event) => {
              onSelectMultipleClick();
            }}
          >
            <Button
              variant={tableHeader ? 'textPlain' : 'outline'}
              shape={tableHeader ? 'rect' : undefined}
              onClick={onSelectMultipleClick}
              className={clsx({
                [styles.actionButton]: !tableHeader,
                [styles.actionButtonActive]: isChecked,
                [styles.tableHeaderActionButton]: tableHeader,
                [styles.tableHeaderActionButtonActive]:
                  tableHeader && isChecked,
              })}
            >
              <span style={{ whiteSpace: 'nowrap' }}>
                <Content id={ContentId.BulkActions} />
              </span>
              {isChecked && (
                <motion.div
                  style={{ justifyContent: 'center', display: 'flex' }}
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <CrossIcon
                    fill={
                      isChecked
                        ? vars.color.textInverted
                        : vars.color.textStrong
                    }
                    size={vars.iconSize.s}
                  />
                </motion.div>
              )}
            </Button>
          </PosHotkeys>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
